import { Mat4 } from '../math/Mat4';
import { Vec3 } from '../math/Vec3';
import type { Engine } from '../core/Engine';
import type { GameModule } from '../core/GameModule';
import type { CameraConfig } from '../config/CameraConfig';

/**
 * Perspective camera with configurable near/far planes and FOV.
 * Config is injected — nothing hardcoded.
 */
export class Camera implements GameModule {
  readonly name = 'Camera';

  readonly projection = new Mat4();
  readonly view = new Mat4();

  readonly position = new Vec3(0, 5, 10);
  readonly target = new Vec3(0, 0, 0);
  readonly up = new Vec3(0, 1, 0);
  yaw = 0;
  pitch = 0;

  /** 6 frustum planes (ax+by+cz+d), 4 floats each, updated every frame. */
  private readonly _frustum = new Float32Array(24);
  private readonly _vp = new Mat4();

  private _config: CameraConfig;
  private _aspect = 1;
  private _dirty = true;

  constructor(config: CameraConfig) {
    this._config = config;
  }

  get fov(): number { return this._config.fov; }

  init(engine: Engine): void {
    const c = engine.canvas;
    this._aspect = c.width / c.height;
    this._updateProjection();
    this._updateView();

    engine.events.on<{ width: number; height: number }>('engine:resize', ({ width, height }) => {
      this._aspect = width / height;
      this._dirty = true;
    });
  }

  update(_dt: number): void {
    if (this._dirty) {
      this._updateProjection();
      this._dirty = false;
    }
    this._updateView();
    this._updateFrustum();
  }

  /** Test if a bounding sphere is (partially) inside the view frustum. */
  sphereInFrustum(x: number, y: number, z: number, radius: number): boolean {
    const f = this._frustum;
    for (let i = 0; i < 24; i += 4) {
      if (f[i] * x + f[i + 1] * y + f[i + 2] * z + f[i + 3] < -radius) return false;
    }
    return true;
  }

  dispose(): void {}

  private _updateProjection(): void {
    this.projection.perspective(this._config.fov, this._aspect, 0.1, this._config.farPlane);
  }

  private _updateView(): void {
    this.view.lookAt(this.position, this.target, this.up);
  }

  /** Extract 6 frustum planes from the VP matrix (Gribb/Hartmann). */
  private _updateFrustum(): void {
    const vp = this._vp;
    vp.data.set(this.projection.data);
    vp.multiply(this.view);
    const m = vp.data;
    const f = this._frustum;

    // Left, Right, Bottom, Top, Near, Far
    f[0]  = m[3] + m[0];  f[1]  = m[7] + m[4];  f[2]  = m[11] + m[8];  f[3]  = m[15] + m[12];
    f[4]  = m[3] - m[0];  f[5]  = m[7] - m[4];  f[6]  = m[11] - m[8];  f[7]  = m[15] - m[12];
    f[8]  = m[3] + m[1];  f[9]  = m[7] + m[5];  f[10] = m[11] + m[9];  f[11] = m[15] + m[13];
    f[12] = m[3] - m[1];  f[13] = m[7] - m[5];  f[14] = m[11] - m[9];  f[15] = m[15] - m[13];
    f[16] = m[3] + m[2];  f[17] = m[7] + m[6];  f[18] = m[11] + m[10]; f[19] = m[15] + m[14];
    f[20] = m[3] - m[2];  f[21] = m[7] - m[6];  f[22] = m[11] - m[10]; f[23] = m[15] - m[14];

    for (let i = 0; i < 24; i += 4) {
      const len = Math.sqrt(f[i] * f[i] + f[i + 1] * f[i + 1] + f[i + 2] * f[i + 2]);
      if (len > 1e-8) { f[i] /= len; f[i + 1] /= len; f[i + 2] /= len; f[i + 3] /= len; }
    }
  }
}
